import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './styles/globals.css';

// Lazy load pages (원점 재설계 버전)
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const WorkerHomePage = lazy(() => import('./pages/WorkerHomePage').then(m => ({ default: m.WorkerHomePage })));
const CheckinPage = lazy(() => import('./pages/CheckinPage').then(m => ({ default: m.CheckinPage })));
const RecordsPage = lazy(() => import('./pages/RecordsPage').then(m => ({ default: m.RecordsPage })));

const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })));

// 기존 페이지들 (임시 유지 혹은 순차 전환)
const CheckinComplete = lazy(() => import('./pages/worker/CheckinComplete').then(m => ({ default: m.CheckinComplete })));
const CheckinDetail = lazy(() => import('./pages/worker/CheckinDetail').then(m => ({ default: m.CheckinDetail })));
const EditCheckin = lazy(() => import('./pages/worker/EditCheckin').then(m => ({ default: m.EditCheckin })));
const NoticeDetail = lazy(() => import('./pages/worker/NoticeDetail').then(m => ({ default: m.NoticeDetail })));

const WorkerDetail = lazy(() => import('./pages/admin/WorkerDetail').then(m => ({ default: m.WorkerDetail })));
const WorkerManagement = lazy(() => import('./pages/admin/WorkerManagement').then(m => ({ default: m.WorkerManagement })));
const NoticeManagement = lazy(() => import('./pages/admin/NoticeManagement').then(m => ({ default: m.NoticeManagement })));
const MessageCenter = lazy(() => import('./pages/admin/MessageCenter').then(m => ({ default: m.MessageCenter })));
const ChangePasswordPage = lazy(() => import('./pages/admin/ChangePasswordPage').then(m => ({ default: m.ChangePasswordPage })));
const SignupApprovalPage = lazy(() => import('./pages/admin/SignupApprovalPage').then(m => ({ default: m.SignupApprovalPage })));
const PrivacyManagementPage = lazy(() => import('./pages/admin/PrivacyManagementPage').then(m => ({ default: m.PrivacyManagementPage })));
const AttendanceManagementPage = lazy(() => import('./pages/admin/AttendanceManagementPage').then(m => ({ default: m.AttendanceManagementPage })));

// 세련된 로딩 화면
const Loading: React.FC = () => (
  <div className="flex flex-col items-center justify-center min-h-screen gap-4">
    <div className="text-4xl animate-float">🏢</div>
    <p className="text-sm font-bold text-indigo-600 tracking-tight select-none">데이터를 준비하고 있습니다...</p>
  </div>
);

// 보호된 라우트
const ProtectedRoute: React.FC<{ children: React.ReactNode; userType: 'worker' | 'admin' }> = ({
  children,
  userType,
}) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.type !== userType) {
    return <Navigate to="/" replace />;
  }

  if (user.type === 'admin' && user.mustChangePassword && location.pathname !== '/admin/change-password') {
    return <Navigate to="/admin/change-password" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => (
  <Suspense fallback={<Loading />}>
    <Routes>
      {/* 기본 홈: LoginPage 내에서 인증 상태에 따라 라우팅 */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/worker/login" element={<LoginPage />} />
      <Route path="/admin/login" element={<LoginPage />} />

      {/* 취업자 라우트 */}
      <Route path="/worker/main" element={
        <ProtectedRoute userType="worker"><WorkerHomePage /></ProtectedRoute>
      } />
      <Route path="/worker/checkin" element={
        <ProtectedRoute userType="worker"><CheckinPage /></ProtectedRoute>
      } />
      <Route path="/worker/checkin/complete" element={
        <ProtectedRoute userType="worker"><CheckinComplete /></ProtectedRoute>
      } />
      <Route path="/worker/records" element={
        <ProtectedRoute userType="worker"><RecordsPage /></ProtectedRoute>
      } />
      <Route path="/worker/records/:date" element={
        <ProtectedRoute userType="worker"><CheckinDetail /></ProtectedRoute>
      } />
      <Route path="/worker/records/:date/edit" element={
        <ProtectedRoute userType="worker"><EditCheckin /></ProtectedRoute>
      } />
      <Route path="/worker/notice/:noticeId" element={
        <ProtectedRoute userType="worker"><NoticeDetail /></ProtectedRoute>
      } />

      {/* 관리자 라우트 */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute userType="admin"><AdminDashboardPage /></ProtectedRoute>
      } />
      <Route path="/admin/workers/:workerId" element={
        <ProtectedRoute userType="admin"><WorkerDetail /></ProtectedRoute>
      } />
      <Route path="/admin/workers" element={
        <ProtectedRoute userType="admin"><WorkerManagement /></ProtectedRoute>
      } />
      <Route path="/admin/notices" element={
        <ProtectedRoute userType="admin"><NoticeManagement /></ProtectedRoute>
      } />
      <Route path="/admin/messages" element={
        <ProtectedRoute userType="admin"><MessageCenter /></ProtectedRoute>
      } />
      <Route path="/admin/change-password" element={
        <ProtectedRoute userType="admin"><ChangePasswordPage /></ProtectedRoute>
      } />
      <Route path="/admin/approvals" element={
        <ProtectedRoute userType="admin"><SignupApprovalPage /></ProtectedRoute>
      } />
      <Route path="/admin/privacy" element={
        <ProtectedRoute userType="admin"><PrivacyManagementPage /></ProtectedRoute>
      } />
      <Route path="/admin/attendance" element={
        <ProtectedRoute userType="admin"><AttendanceManagementPage /></ProtectedRoute>
      } />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </Suspense>
);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
